import { useState } from 'react';
import { api, sizeLabel, usd } from '../lib/api';
import { ErrorNote, useLoad } from '../components/ui';
import { barcodeSvg, printLabels, TEMPLATES, type LabelTemplate } from '../lib/labels';
import ShelfTalkers from './ShelfTalkers';

type Kind = 'needs_label' | 'price_changed' | 'new' | 'search';
interface Row { id: string; brand: string; name: string; size_ml: number; units_per_pack: number; retail_cents: number; code: string | null; changed_at: string | null; reason?: string | null }

// Shelf labels: price tags with a barcode, and shelf talkers (signs that sell a product).
export default function Labels() {
  const [mode, setMode] = useState<'tags' | 'talkers'>('tags');
  return (
    <div className="stack">
      <div className="row wrap" role="tablist">
        <button role="tab" aria-selected={mode === 'tags'} className={mode === 'tags' ? 'primary' : ''} onClick={() => setMode('tags')}>Price tags</button>
        <button role="tab" aria-selected={mode === 'talkers'} className={mode === 'talkers' ? 'primary' : ''} onClick={() => setMode('talkers')}>Shelf talkers (AI)</button>
      </div>
      {mode === 'tags' ? <PriceTags /> : <div className="stack"><div className="page-head"><div className="grow"><h1>Shelf talkers</h1></div></div><ShelfTalkers /></div>}
    </div>
  );
}

function PriceTags() {
  const [kind, setKind] = useState<Kind>('needs_label');
  const [days, setDays] = useState(7);
  const [q, setQ] = useState('');
  const [template, setTemplate] = useState<LabelTemplate>('tag');
  const [picked, setPicked] = useState<Record<string, number>>({});   // item id -> copies
  const [keep, setKeep] = useState<Record<string, Row>>({});
  const list = useLoad(() => api<Row[]>(`/api/labels/candidates?kind=${kind}&days=${days}${kind === 'search' ? `&q=${encodeURIComponent(q)}` : ''}`), [kind, days, q]);
  const [msg, setMsg] = useState('');
  const [asked, setAsked] = useState(false);
  const [busy, setBusy] = useState(false);

  const rows = list.data ?? [];
  const toggle = (r: Row) => {
    setKeep((k) => ({ ...k, [r.id]: r }));
    setPicked((p) => { const n = { ...p }; if (n[r.id]) delete n[r.id]; else n[r.id] = 1; return n; });
  };
  const all = () => { setKeep((k) => ({ ...k, ...Object.fromEntries(rows.map((r) => [r.id, r])) })); setPicked((p) => ({ ...p, ...Object.fromEntries(rows.map((r) => [r.id, p[r.id] ?? 1])) })); };
  const chosen = Object.keys(picked).map((id) => keep[id]!).filter(Boolean);
  const copies = chosen.reduce((s, r) => s + (picked[r.id] ?? 1), 0);

  function print() {
    setMsg('');
    const ok = printLabels(chosen.map((r) => ({ ...r, copies: picked[r.id] ?? 1 })), template);
    if (!ok) setMsg('The browser blocked the print window. Allow pop-ups for this site and try again.'); else setAsked(true);
  }
  // One click: every item whose name or price changed since its last label (and every new item), selected and ready to print.
  async function addChanged() {
    setMsg(''); setAsked(false); setBusy(true);
    try {
      const rs = await api<Row[]>('/api/labels/candidates?kind=needs_label');
      setKind('needs_label');
      setKeep((k) => ({ ...k, ...Object.fromEntries(rs.map((r) => [r.id, r])) }));
      setPicked((p) => ({ ...p, ...Object.fromEntries(rs.map((r) => [r.id, p[r.id] ?? 1])) }));
      setMsg(rs.length ? `${rs.length} item${rs.length === 1 ? '' : 's'} added: ${rs.filter((r) => r.reason?.includes('price')).length} with a new price, ${rs.filter((r) => r.reason?.includes('name')).length} with a new name, ${rs.filter((r) => r.reason === 'new item').length} new.` : 'Every label is up to date. Nothing has changed since the last print.');
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Could not load the list.'); } finally { setBusy(false); }
  }
  async function markPrinted() {
    try {
      await api('/api/labels/printed', { body: { itemIds: chosen.map((r) => r.id) } });
      setPicked({}); setAsked(false); setMsg('Done. Those items are off the list until their name or price changes again.'); list.reload();
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Could not save.'); }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div className="grow"><h1>Shelf labels</h1><p>Print price tags with a scannable barcode. One click adds everything whose name or price changed since its last label.</p></div>
      </div>
      <div className="row wrap">
        <button className="primary" disabled={busy} onClick={addChanged}>{busy ? 'Looking…' : 'Add everything that needs a new label'}</button>
        <select aria-label="Which items" style={{ width: 'auto' }} value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
          <option value="needs_label">Needs a new label (price or name changed)</option><option value="price_changed">Price changed recently</option><option value="new">Added recently</option><option value="search">Search items</option>
        </select>
        {kind !== 'search' && kind !== 'needs_label' && <select aria-label="How recently" style={{ width: 'auto' }} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[1, 3, 7, 14, 30, 90].map((d) => <option key={d} value={d}>last {d} day{d > 1 ? 's' : ''}</option>)}
        </select>}
        {kind === 'search' && <input style={{ maxWidth: 320 }} autoFocus placeholder="Name or brand" value={q} onChange={(e) => setQ(e.target.value)} />}
        <select aria-label="Label size" style={{ width: 'auto' }} value={template} onChange={(e) => setTemplate(e.target.value as LabelTemplate)}>
          {Object.entries(TEMPLATES).map(([k, t]) => <option key={k} value={k}>{t.name}</option>)}
        </select>
        <button onClick={all} disabled={!rows.length}>Select all shown</button>
        <button className="primary" disabled={!chosen.length} onClick={print}>Print {copies} label{copies === 1 ? '' : 's'}</button>
      </div>
      <p className="faint">{TEMPLATES[template].hint} In the print window, set margins to “None” and scale to 100%.</p>
      {msg && <div className={`notice ${/blocked|Could not/.test(msg) ? 'bad' : 'good'}`}>{msg}</div>}
      {asked && chosen.length > 0 && <div className="notice">After the labels print correctly, press this so they come off the list: <button className="primary" onClick={markPrinted}>They printed fine</button></div>}
      <ErrorNote error={list.error} />
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th /><th>Product</th><th className="n">Price</th><th>Barcode</th><th className="n">Copies</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id}>
              <td><input type="checkbox" style={{ width: 'auto', minHeight: 0 }} checked={!!picked[r.id]} onChange={() => toggle(r)} aria-label={`Select ${r.brand} ${r.name}`} /></td>
              <td><div style={{ fontWeight: 600 }}>{r.brand} {r.name}</div><div className="faint">{sizeLabel(r.size_ml)}{r.changed_at ? ` · changed ${new Date(r.changed_at).toLocaleDateString()}` : ''}{r.reason && <span className="chip warn" style={{ marginLeft: 6 }}>{r.reason}</span>}</div></td>
              <td className="n">{usd(r.retail_cents)}</td>
              <td style={{ width: 120 }}>{barcodeSvg(r.code, 24) ? <div className="mini-bc" dangerouslySetInnerHTML={{ __html: barcodeSvg(r.code, 24) }} /> : <span className="chip warn">No barcode</span>}</td>
              <td className="n">{picked[r.id] ? <input style={{ width: 56, textAlign: 'right' }} inputMode="numeric" value={picked[r.id]} onChange={(e) => setPicked({ ...picked, [r.id]: Math.max(1, Math.min(99, Number(e.target.value.replace(/\D/g, '')) || 1)) })} /> : ''}</td>
            </tr>))}</tbody>
        </table>
        {list.data && rows.length === 0 && <div className="empty">{kind === 'search' ? 'Type at least two letters.' : kind === 'needs_label' ? 'Every label is up to date. Change a price or a name and it shows up here.' : 'Nothing here. Change a price and it will show up.'}</div>}
      </div></div>
    </div>
  );
}
